import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { message, Spin, Modal, Tag, Button, Divider, Tooltip, Dropdown, Menu, Upload, Form, Input, Select } from "antd";
import { PrinterOutlined, EditOutlined, FileOutlined, MoreOutlined, CloudUploadOutlined, FolderOpenOutlined, InboxOutlined } from "@ant-design/icons";
import ReactDOMServer from "react-dom/server";
import LoadingSpinner from "../shared/LoadingSpinner";
import ErrorMessage from "../shared/ErrorMessage";
import {
  casesService,
  documentsService,
  sessionsService,
  paymentsService,
  expensesService,
  opponentsService,
  clientsService,
} from "../../services/api.service";
import PrintCaseDetails from "./PrintCaseDetails";
import CaseSessions from "./CaseSessions";
import DocumentsList from "..//DocumentsList";
import PaymentsTable from "../payments/PaymentsTable";
import PaymentDetails from "../payments/PaymentDetails";
import AddPaymentModal from "../payments/addPayment";
import AddExpenseModal from "../expenses/AddExpenseModal";
import EditExpenseModal from "../expenses/EditExpenseModal";
import ViewExpenseModal from "../expenses/ViewExpenseModal";
import ExpensesTable from "../expenses/ExpensesTable";
import { Helmet } from "react-helmet-async";
import ErrorBoundary from "../ErrorBoundary";
import EditCaseModal from "./EditCaseModal";
import moment from "moment";
import logger from "../../utils/logger";

export default function CaseDetails({ caseId }) {
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentDetailsVisible, setIsPaymentDetailsVisible] = useState(false);
  const [isAddPaymentModalVisible, setIsAddPaymentModalVisible] =
    useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [isAddExpenseModalVisible, setIsAddExpenseModalVisible] =
    useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isViewExpenseModalVisible, setIsViewExpenseModalVisible] =
    useState(false);
  const [isEditExpenseModalVisible, setIsEditExpenseModalVisible] =
    useState(false);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);
  
  // Document upload states
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [showFileDetailsModal, setShowFileDetailsModal] = useState(false);
  const [documentDetails, setDocumentDetails] = useState({
    title: "",
    document_type: "",
    notes: "",
  });

  // Status color mapping for visual representation
  const statusColors = {
    جديدة: { bg: "bg-purple-100", text: "text-purple-800", color: "purple" },
    نشط: { bg: "bg-emerald-100", text: "text-emerald-800", color: "green" },
    جارية: { bg: "bg-emerald-100", text: "text-emerald-800", color: "green" },
    "قيد النظر": { bg: "bg-blue-100", text: "text-blue-800", color: "blue" },
    معلقة: { bg: "bg-amber-100", text: "text-amber-800", color: "orange" },
    معلق: { bg: "bg-amber-100", text: "text-amber-800", color: "orange" },
    مغلقة: { bg: "bg-gray-100", text: "text-gray-800", color: "default" },
    مغلق: { bg: "bg-gray-100", text: "text-gray-800", color: "default" },
    مكتملة: { bg: "bg-green-100", text: "text-green-800", color: "green" },
  };

  // Tabs for navigating different sections of case details
  const tabs = [
    { id: "details", label: "معلومات القضية", icon: "📄" },
    { id: "sessions", label: "الجلسات والمواعيد", icon: "⚖️" },
    { id: "expenses", label: "المصاريف", icon: "📊" },
    { id: "payments", label: "المدفوعات", icon: "💵" },
    { id: "documents", label: "الملفات والمستندات", icon: "📁" },
    { id: "notes", label: "الملاحظات", icon: "📝" },
  ];

  // Fetch case details and opponent details on component mount
  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        setLoading(true);
        const data = await casesService.getCaseDetails(caseId);

        let opponent = null;

        try {
          // جلب معلومات الخصم باستخدام opponent_id
          if (data.opponent_id) {
            logger.debug("Fetching opponent with ID:", data.opponent_id);
            const opponentResponse = await opponentsService.getOpponentById(
              data.opponent_id
            );
            if (opponentResponse.data) {
              opponent = opponentResponse.data;
            }
          }
          logger.debug("Opponent details:", opponent);
        } catch (error) {
          logger.error("Error fetching opponent:", error);
        }

        // جلب تفاصيل العميل
        let client = null;
        try {
          if (data.client_id) {
            logger.debug("Fetching client with ID:", data.client_id);
            const clientResponse = await clientsService.getClientById(
              data.client_id
            );
            if (clientResponse) {
              client = clientResponse;
            }
          }
          logger.debug("Client details:", client);
        } catch (error) {
          logger.error("Error fetching client:", error);
        }

        setCaseDetails({
          ...data,
          sessions: data.sessions || [],
          opponent: opponent,
        });

        setClientDetails(client);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseDetails();
  }, [caseId]);

  // Fetch documents when documents tab is active
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentsService.getDocumentsByCaseId(caseId);
      setDocuments(response.data || []);
    } catch (error) {
      logger.error("Error fetching documents:", error);
      message.error("فشل في جلب المستندات");
    } finally {
      setLoading(false);
    }
  };

  // Fetch sessions when sessions tab is active
  const fetchSessions = async () => {
    if (activeTab === "sessions" || activeTab === "notes") {
      try {
        setSessionsLoading(true);
        const data = await sessionsService.getSessionsByCase(caseId);
        setSessions(Array.isArray(data) ? data : []);
      } catch (error) {
        logger.error("Error fetching sessions:", error);
        message.error("فشل في جلب الجلسات");
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    }
  };

  // Fetch payments when payments tab is active
  const fetchPayments = async () => {
    if (activeTab === "payments") {
      try {
        setPaymentsLoading(true);
        logger.debug("جاري جلب مدفوعات القضية:", caseId);
        // استخدام paymentsService لجلب مدفوعات القضية
        const response = await paymentsService.getPaymentsByCase(caseId);
        logger.debug("استجابة المدفوعات:", response);

        // التعامل مع بنية البيانات الجديدة
        if (response && response.success && Array.isArray(response.data)) {
          logger.debug("عدد المدفوعات:", response.data.length);
          setPayments(response.data);
        } else {
          logger.debug("بنية الاستجابة غير متوقعة:", response);
          setPayments([]);
        }
      } catch (error) {
        logger.error("Error fetching payments:", error);
        message.error("فشل في جلب المدفوعات");
        setPayments([]);
      } finally {
        setPaymentsLoading(false);
      }
    }
  };

  // Fetch expenses when expenses tab is active
  const fetchExpenses = async () => {
    if (activeTab === "expenses") {
      try {
        setExpensesLoading(true);
        logger.debug("جاري جلب مصاريف القضية:", caseId);
        // استخدام expensesService لجلب مصاريف القضية
        const data = await expensesService.getExpensesByCaseId(caseId);
        logger.debug("استجابة المصاريف:", data);

        // التعامل مع بنية البيانات
        if (Array.isArray(data)) {
          logger.debug("عدد المصاريف:", data.length);
          setExpenses(data);
        } else {
          logger.debug("بنية الاستجابة غير متوقعة:", data);
          setExpenses([]);
        }
      } catch (error) {
        logger.error("Error fetching expenses:", error);
        message.error("فشل في جلب المصاريف");
        setExpenses([]);
      } finally {
        setExpensesLoading(false);
      }
    }
  };

  // وظيفة عرض تفاصيل الدفعة
  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setIsPaymentDetailsVisible(true);
  };

  // وظيفة حذف الدفعة
  const handleDeletePayment = async (paymentId) => {
    Modal.confirm({
      title: "تأكيد الحذف",
      content: "هل أنت متأكد من أنك تريد حذف هذه الدفعة؟",
      okText: "حذف",
      cancelText: "إلغاء",
      okType: "danger",
      onOk: async () => {
        try {
          await paymentsService.deletePayment(paymentId);
          message.success("تم حذف الدفعة بنجاح");
          // إعادة جلب المدفوعات بعد الحذف
          fetchPayments();
        } catch (error) {
          logger.error("Error deleting payment:", error);
          message.error("فشل في حذف الدفعة");
        }
      },
    });
  };

  // وظيفة فتح مودل إضافة الدفعة
  const handleAddPayment = () => {
    setIsAddPaymentModalVisible(true);
  };

  // وظيفة فتح مودل إضافة المصروف
  const handleAddExpense = () => {
    setIsAddExpenseModalVisible(true);
  };

  // وظيفة فتح مودل تعديل المصروف
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setIsEditExpenseModalVisible(true);
  };

  // وظيفة فتح مودل عرض المصروف
  const handleViewExpense = (expense) => {
    setViewingExpense(expense);
    setIsViewExpenseModalVisible(true);
  };

  // وظيفة تحديث المصروف بعد التعديل
  const handleExpenseUpdated = () => {
    setIsEditExpenseModalVisible(false);
    fetchExpenses();
    message.success("تم تحديث المصروف بنجاح");
  };

  // وظيفة إغلاق نافذة عرض المصروف
  const handleCloseViewExpense = () => {
    setIsViewExpenseModalVisible(false);
    setViewingExpense(null);
  };

  // وظيفة إغلاق نافذة تعديل المصروف
  const handleCloseEditExpense = () => {
    setIsEditExpenseModalVisible(false);
    setEditingExpense(null);
  };

  // Load data when active tab changes
  useEffect(() => {
    fetchDocuments();
    if (activeTab === "sessions" || activeTab === "notes") {
      fetchSessions();
    } else if (activeTab === "payments") {
      fetchPayments();
    } else if (activeTab === "expenses") {
      fetchExpenses();
    }
  }, [activeTab, caseId]);

  // Handle printing case details
  const handlePrint = async () => {
    try {
      // عرض رسالة تحميل
      const loadingMessage = message.loading('جاري تحميل بيانات القضية للطباعة...', 0);

      // جلب البيانات الأساسية للقضية مرة أخرى لضمان الحصول على أحدث البيانات
      let allData = { ...caseDetails };

      try {
        const latestCaseData = await casesService.getCaseDetails(caseId);
        logger.debug("Latest case data for printing:", latestCaseData);
        allData = { ...allData, ...latestCaseData };
      } catch (error) {
        console.error("Error fetching latest case details:", error);
      }

      // جلب بيانات العميل والخصم إذا لم تكن موجودة
      if (!allData.clientDetails && allData.client_id) {
        try {
          const clientResponse = await clientsService.getClientById(allData.client_id);
          if (clientResponse) {
            allData.clientDetails = clientResponse;
          }
        } catch (error) {
          logger.error("Error fetching client details:", error);
        }
      }

      if (!allData.opponent && allData.opponent_id) {
        try {
          const opponentResponse = await opponentsService.getOpponentById(allData.opponent_id);
          if (opponentResponse && opponentResponse.data) {
            allData.opponent = opponentResponse.data;
          }
        } catch (error) {
          logger.error("Error fetching opponent details:", error);
        }
      }

      // جلب الجلسات
      try {
        const sessionsData = await sessionsService.getSessionsByCase(caseId);
        allData.sessions = Array.isArray(sessionsData) ? sessionsData : [];
      } catch (error) {
        logger.error("Error fetching sessions for printing:", error);
        allData.sessions = [];
      }

      // جلب المستندات
      try {
        const documentsData = await documentsService.getDocumentsByCaseId(caseId);
        allData.documents = documentsData.data || [];
      } catch (error) {
        logger.error("Error fetching documents for printing:", error);
        allData.documents = [];
      }

      // جلب المدفوعات
      try {
        const paymentsData = await paymentsService.getPaymentsByCase(caseId);
        if (paymentsData && paymentsData.success && Array.isArray(paymentsData.data)) {
          allData.payments = paymentsData.data;
        } else {
          allData.payments = [];
        }
      } catch (error) {
        logger.error("Error fetching payments for printing:", error);
        allData.payments = [];
      }

      // جلب المصاريف
      try {
        const expensesData = await expensesService.getExpensesByCaseId(caseId);
        allData.expenses = Array.isArray(expensesData) ? expensesData : [];
      } catch (error) {
        logger.error("Error fetching expenses for printing:", error);
        allData.expenses = [];
      }

      // إغلاق رسالة التحميل
      loadingMessage();

      // طباعة البيانات للتحقق
      logger.debug("All data for printing:", allData);
      logger.debug("Sessions count:", allData.sessions?.length || 0);
      logger.debug("Documents count:", allData.documents?.length || 0);
      logger.debug("Payments count:", allData.payments?.length || 0);
      logger.debug("Expenses count:", allData.expenses?.length || 0);

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("يرجى السماح بفتح النوافذ المنبثقة لتتمكن من الطباعة");
        return;
      }

      const printContent = ReactDOMServer.renderToString(
        <PrintCaseDetails caseData={allData} />
      );

      printWindow.document.write(`
                <html dir="rtl">
                    <head>
                        <title>طباعة تفاصيل القضية - ${allData.case_number || allData.number}</title>
                        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
                        <style>
                            @media print {
                                body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                                @page { size: A4; margin: 2cm; }
                            }
                        </style>
                    </head>
                    <body>
                        ${printContent}
                    </body>
                </html>
            `);

      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = function () {
          printWindow.close();
        };
      }, 1000);
    } catch (error) {
      logger.error("خطأ في الطباعة:", error);
      alert("حدث خطأ أثناء محاولة الطباعة");
    }
  };

  // إضافة دالة لتحديث معلومات القضية
  const updateCaseDetails = async () => {
    try {
      const data = await casesService.getCaseDetails(caseId);
      setCaseDetails((prev) => ({
        ...prev,
        ...data,
      }));
    } catch (err) {
      logger.error("Error updating case details:", err);
      message.error("فشل في تحديث معلومات القضية");
    }
  };
  const handleAddSession = async () => {
    try {
      // التحديث الفعلي للقائمة لأن AddSessionModal يقوم بالحفظ تلقائياً
      await fetchSessions();
      
      try {
        await updateNextSessionDate();
        await updateCaseDetails();
      } catch (error) {
        logger.error("Error updating case details:", error);
      }
    } catch (error) {
      logger.error("Error refreshing sessions:", error);
    }
  };

  // تحديث دوال إدارة الجلسات
  const handleEditSession = async () => {
    try {
      // تحديث البيانات لأن EditSessionModal يقوم بالحفظ تلقائياً
      await fetchSessions();
      await updateNextSessionDate();
      await updateCaseDetails();
    } catch (error) {
      logger.error("Error updating session:", error);
      message.error(error.message || "فشل في تحديث الجلسة");
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await sessionsService.deleteSession(sessionId);
      message.success("تم حذف الجلسة بنجاح");
      await fetchSessions();
      await updateNextSessionDate();
      await updateCaseDetails();
    } catch (error) {
      logger.error("Error deleting session:", error);
      message.error("فشل في حذف الجلسة");
    }
  };

  // Handle editing case details
  const handleEdit = () => {
    // تحويل التواريخ من التنسيق العربي إلى كائن moment
    const formInitialData = {
      ...caseDetails,
      case_number: caseDetails.case_number,
      title: caseDetails.title,
      client_id: caseDetails.client_id,
      client_name: caseDetails.client_name,
      opponent_name: caseDetails.opponent_name,
      court_name: caseDetails.court_name,
      case_type: caseDetails.case_type,
      status: caseDetails.status,
      // استخدام التاريخ الأصلي من الخادم قبل التنسيق
      filing_date: caseDetails.filing_date_original
        ? moment(caseDetails.filing_date_original)
        : null,
      next_session_date: caseDetails.next_session_date_original
        ? moment(caseDetails.next_session_date_original)
        : null,
      description: caseDetails.description,
      notes: caseDetails.notes,
      agreed_fees: caseDetails.agreed_fees,
      court_case_number: caseDetails.court_case_number,
      phone: caseDetails.phone,
    };

    setSelectedCase(formInitialData);
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      setLoading(true);

      const formatDate = (dateValue) => {
        if (!dateValue) return null;
        return moment(dateValue).locale("en").format("YYYY-MM-DD");
      };

      const formData = {
        ...values,
        filing_date: formatDate(values.filing_date),
        next_session_date: formatDate(values.next_session_date),
        case_number: values.case_number?.trim(),
        title: values.title?.trim(),
        client_id: values.client_id,
        client_name: values.client_name?.trim(),
        opponent_name: values.opponent_name?.trim(),
        court_name: values.court_name?.trim(),
        case_type: values.case_type?.trim(),
        status: values.status,
        description: values.description?.trim(),
        notes: values.notes?.trim(),
        phone: values.phone?.trim(),
        court_case_number: values.court_case_number?.trim(),
        agreed_fees: values.agreed_fees ? parseFloat(values.agreed_fees) : null,
      };

      logger.debug("Submitting to API:", formData); // للتأكد من البيانات

      const response = await casesService.updateCase(caseId, formData);

      if (response.success) {
        message.success("تم تحديث القضية بنجاح");
        setIsEditModalVisible(false);
        await updateCaseDetails();
      }
    } catch (error) {
      logger.error("Error updating case:", error);
      message.error("فشل في تحديث القضية");
    } finally {
      setLoading(false);
    }
  };

  // دالة معاينة الملف قبل الرفع
  const handleFilePreview = async (file) => {
    if (!file) return false;

    // التحقق من نوع الملف (مع دعم Excel)
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/zip",
      "application/x-rar-compressed",
      "application/x-zip-compressed",
    ];
    if (!allowedTypes.includes(file.type)) {
      message.error(
        "نوع الملف غير مدعوم. الرجاء رفع ملفات PDF, Word, Excel, صور, أو ملفات مضغوطة فقط"
      );
      return false;
    }

    // التحقق من حجم الملف (حد أقصى 10 ميجا)
    if (file.size > 10 * 1024 * 1024) {
      message.error("حجم الملف كبير جداً. الحد الأقصى 10 ميجابايت");
      return false;
    }

    // تحديد نوع المستند تلقائياً
    let documentType = "مستند عام";
    if (file.type.includes("pdf")) documentType = "ملف PDF";
    else if (file.type.includes("word")) documentType = "مستند Word";
    else if (file.type.includes("excel") || file.type.includes("sheet"))
      documentType = "جدول Excel";
    else if (file.type.includes("image")) documentType = "صورة";
    else if (file.type.includes("zip") || file.type.includes("rar"))
      documentType = "ملف مضغوط";

    // إعداد بيانات الملف
    setFileToUpload(file);
    setDocumentDetails({
      title: file.name.split(".")[0], // إزالة الامتداد من الاسم
      document_type: documentType,
      notes: "",
    });
    setShowFileDetailsModal(true);

    return false; // منع الرفع التلقائي
  };

  // دالة رفع الملف الفعلي مع التفاصيل
  const handleConfirmUpload = async () => {
    if (!fileToUpload) return;

    setUploading(true);
    try {
      // رفع المستند مع البيانات الإضافية
      await documentsService.uploadDocument(
        caseId,
        fileToUpload,
        documentDetails,
        caseDetails?.client_id
      );

      message.success(`تم رفع "${documentDetails.title}" بنجاح!`);
      await fetchDocuments();
      setShowUploadArea(false);
      setShowFileDetailsModal(false);
      setFileToUpload(null);
      setDocumentDetails({ title: "", document_type: "", notes: "" });
    } catch (error) {
      logger.error("Error uploading file:", error);
      message.error(
        "فشل في رفع الملف: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setUploading(false);
    }
  };

  // دالة تنفذ بعد نجاح رفع الملف
  const handleUploadSuccess = async () => {
    message.success("تم رفع المستند بنجاح!");
    await fetchDocuments(); // تحديث قائمة المستندات
  };

  // دالة لرفع الملف مباشرة (في حالة عدم استخدام مكون FileUpload)
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) {
      message.error("يرجى اختيار ملف للرفع");
      return;
    }

    // التحقق من حجم الملف
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      message.error("حجم الملف يتجاوز الحد المسموح به (5 ميجابايت)");
      return;
    }

    try {
      setLoading(true);
      // إرسال معرف العميل مع الطلب
      await documentsService.uploadDocument(
        caseId,
        file,
        caseDetails?.client_id
      );
      message.success("تم رفع المستند بنجاح!");

      // تفريغ قيمة حقل الملف
      event.target.value = "";

      // تحديث قائمة المستندات
      await fetchDocuments();
    } catch (error) {
      logger.error("Error uploading document:", error);
      message.error(error.response?.data?.error || "فشل في رفع المستند");
    } finally {
      setLoading(false);
    }
  };

  // توحيد دالة حذف المستندات

  const handleDeleteDocument = async (docId) => {
    try {
      // تأكيد العملية قبل الحذف
      Modal.confirm({
        title: "تأكيد الحذف",
        content:
          "هل أنت متأكد من حذف هذا المستند؟ لا يمكن التراجع عن هذه العملية.",
        okText: "نعم، احذف",
        okType: "danger",
        cancelText: "إلغاء",
        onOk: async () => {
          setLoading(true);
          try {
            // تحقق إذا كان docId كائن كامل أو معرف فقط
            const documentId = typeof docId === "object" ? docId.id : docId;

            await documentsService.deleteDocument(caseId, documentId);
            message.success("تم حذف المستند بنجاح");
            await fetchDocuments(); // تحديث قائمة المستندات
          } catch (error) {
            logger.error("Error deleting document:", error);
            message.error("فشل في حذف المستند");
          } finally {
            setLoading(false);
          }
        },
      });
    } catch (error) {
      logger.error("Error in delete confirmation:", error);
      message.error("حدث خطأ في عملية الحذف");
    }
  };

  // Document management functions
  const handleFileDownload = async (doc) => {
    try {
      const documentId = typeof doc === "object" ? doc.id : doc;
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      // استخدام المسار الصحيح للتحميل
      const downloadUrl = `${baseURL}/documents/${documentId}/download`;

      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("فشل في تحميل الملف");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Get filename from headers
      const contentDisposition = response.headers.get("content-disposition");
      const filenameMatch =
        contentDisposition &&
        contentDisposition.match(/filename\*=UTF-8''(.*)/);
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1])
        : `document-${documentId}`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success("تم بدء تحميل الملف");
    } catch (error) {
      console.error("Error downloading document:", error);
      message.error("فشل في تحميل المستند");
    }
  };

  // إضافة دالة معاينة المستند
  const handlePreviewDocument = (doc) => {
    const documentId = typeof doc === "object" ? doc.id : doc;
    setSelectedDocument({
      ...doc,
      previewUrl: `${
        process.env.REACT_APP_API_URL || "http://localhost:5000"
      }/documents/${documentId}/preview`,
    });
    setIsPreviewVisible(true);
  };

  // دالة لتحديث تاريخ الجلسة القادمة للقضية
  const updateNextSessionDate = async () => {
    try {
      // Get updated sessions
      const updatedSessions = await sessionsService.getSessionsByCase(caseId);

      // Filter and sort future sessions
      const futureSessions = updatedSessions
        .filter((session) => {
          const sessionDate = moment(session.date);
          const today = moment().startOf("day");
          return sessionDate.isSameOrAfter(today) && session.status !== "ملغاة";
        })
        .sort((a, b) => moment(a.date).diff(moment(b.date)));

      // Update case with next session date
      if (futureSessions.length > 0) {
        const nextSession = futureSessions[0];
        const nextSessionDate = moment(nextSession.date).format("YYYY-MM-DD");

        // Include all required fields in the update
        await casesService.updateCase(caseId, {
          next_session_date: nextSessionDate,
          // Add other required fields with their current values
          case_number: caseDetails.case_number,
          client_name: caseDetails.client_name,
          case_type: caseDetails.case_type,
          status: caseDetails.status,
        });

        // Update component state
        setCaseDetails((prev) => ({
          ...prev,
          next_session_date: nextSessionDate,
        }));
      } else {
        // If no future sessions, set next_session_date to null
        await casesService.updateCase(caseId, {
          next_session_date: null,
          // Add other required fields with their current values
          case_number: caseDetails.case_number,
          client_name: caseDetails.client_name,
          case_type: caseDetails.case_type,
          status: caseDetails.status,
        });

        setCaseDetails((prev) => ({
          ...prev,
          next_session_date: null,
        }));
      }
    } catch (error) {
      console.error("Error updating next session date:", error);
      message.error("فشل في تحديث تاريخ الجلسة القادمة");
    }
  };

  // Render case details content
  const renderCaseDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* اليمين - معلومات القضية الأساسية ووصف القضية */}
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">
            معلومات القضية الأساسية
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">رقم القضية الداخلي</p>
              <p className="font-medium">
                {caseDetails?.case_number || "غير محدد"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">رقم القضية بالمحكمة</p>
              <p className="font-medium">
                {caseDetails?.court_case_number || "غير محدد"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">المحكمة</p>
              <p className="font-medium">
                {caseDetails?.court_name || "غير محدد"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">نوع القضية</p>
              <p className="font-medium">
                {caseDetails?.case_type || "غير محدد"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">تاريخ التسجيل</p>
              <p className="font-medium">
                {caseDetails?.filing_date || "غير محدد"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">حالة القضية</p>
              <Tag
                color={statusColors[caseDetails?.status]?.color || "default"}
              >
                {caseDetails?.status || "غير محدد"}
              </Tag>
            </div>
            <div className="col-span-1 md:col-span-2">
              <p className="text-sm text-gray-500 mb-1">الأتعاب المتفق عليها</p>
              <p className="font-medium">
                {caseDetails?.agreed_fees
                  ? `${caseDetails.agreed_fees} دينار`
                  : "غير محدد"}
              </p>
            </div>
          </div>
        </div>

        {/* وصف القضية */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">
            وصف القضية
          </h3>
          <p className="text-gray-700 whitespace-pre-line min-h-[100px]">
            {caseDetails?.description || "لا يوجد وصف مضاف للقضية."}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Parties Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">
            معلومات الأطراف
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-3">العميل</h4>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-lg">
                        {clientDetails?.name || "غير محدد"}
                      </p>
                      <div className="flex gap-2 mt-1">
                        <Tag
                          color={
                            clientDetails?.client_type === "مدعي"
                              ? "blue"
                              : "purple"
                          }
                        >
                          {clientDetails?.client_type || "غير محدد"}
                        </Tag>
                        <Tag
                          color={
                            clientDetails?.status === "active"
                              ? "green"
                              : "default"
                          }
                        >
                          {clientDetails?.status === "active"
                            ? "نشط"
                            : "غير نشط"}
                        </Tag>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {clientDetails?.phone ? (
                      <div>
                        <p className="text-gray-500 text-sm">رقم الهاتف</p>
                        <p className="font-medium dir-ltr inline-block">
                          <a
                            href={`https://wa.me/${
                              clientDetails.phone
                                .replace(/[^0-9]/g, "")
                                .startsWith("964")
                                ? clientDetails.phone.replace(/[^0-9]/g, "")
                                : "964" +
                                  clientDetails.phone.replace(/[^0-9]/g, "")
                            }`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:underline flex items-center gap-1"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              className="bi bi-whatsapp"
                              viewBox="0 0 16 16"
                            >
                              <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                            </svg>
                            {clientDetails.phone}
                          </a>
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-500 text-sm">رقم الهاتف</p>
                        <p className="text-gray-500">غير محدد</p>
                      </div>
                    )}

                    {clientDetails?.email && (
                      <div>
                        <p className="text-gray-500 text-sm">
                          البريد الإلكتروني
                        </p>
                        <p className="font-medium">
                          <a
                            href={`mailto:${clientDetails.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {clientDetails.email}
                          </a>
                        </p>
                      </div>
                    )}

                    {clientDetails?.address && (
                      <div>
                        <p className="text-gray-500 text-sm">العنوان</p>
                        <p className="font-medium">{clientDetails.address}</p>
                      </div>
                    )}

                    {(clientDetails?.national_id ||
                      clientDetails?.commercial_register) && (
                      <div>
                        <p className="text-gray-500 text-sm">
                          الرقم الوطني/رقم الهوية
                        </p>
                        <p className="font-medium">
                          {clientDetails?.national_id ||
                            clientDetails?.commercial_register}
                        </p>
                      </div>
                    )}

                    {(clientDetails?.power_of_attorney_number ||
                      clientDetails?.power_of_attorney_date ||
                      clientDetails?.power_of_attorney_record ||
                      clientDetails?.notary_name) && (
                      <div className="mt-4 border-t pt-4">
                        <h4 className="font-medium">معلومات الوكالة</h4>
                        <div className="space-y-1">
                          {clientDetails?.power_of_attorney_number && (
                            <p className="text-sm">
                              <span className="font-medium">رقم الوكالة:</span>{" "}
                              {clientDetails.power_of_attorney_number}
                            </p>
                          )}
                          {clientDetails?.power_of_attorney_date && (
                            <p className="text-sm">
                              <span className="font-medium">
                                تاريخ الوكالة:
                              </span>{" "}
                              {moment(clientDetails.power_of_attorney_date)
                                .locale("en")
                                .format("YYYY/MM/DD")}
                            </p>
                          )}
                          {clientDetails?.power_of_attorney_record && (
                            <p className="text-sm">
                              <span className="font-medium">سجل الوكالة:</span>{" "}
                              {clientDetails.power_of_attorney_record}
                            </p>
                          )}
                          {clientDetails?.notary_name && (
                            <p className="text-sm">
                              <span className="font-medium">الكاتب العدل:</span>{" "}
                              {clientDetails.notary_name}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* معلومات الخصم */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">معلومات الخصم</h4>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {caseDetails?.opponent ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-lg">
                          {caseDetails.opponent.name}
                        </p>
                        <div className="flex gap-2 mt-1">
                          <Tag
                            color={
                              caseDetails.opponent.opponent_type === "مدعى عليه"
                                ? "blue"
                                : "purple"
                            }
                          >
                            {caseDetails.opponent.opponent_type === "مدعى عليه"
                              ? "مدعى عليه"
                              : "مدعي"}
                          </Tag>
                          <Tag
                            color={
                              caseDetails.opponent.status === "active"
                                ? "green"
                                : caseDetails.opponent.status === "settled"
                                ? "blue"
                                : "default"
                            }
                          >
                            {caseDetails.opponent.status === "active"
                              ? "نشط"
                              : caseDetails.opponent.status === "settled"
                              ? "تمت التسوية"
                              : "غير نشط"}
                          </Tag>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="space-y-3">
                        {caseDetails.opponent.phone && (
                          <div>
                            <p className="text-gray-500 text-sm">رقم الهاتف</p>
                            <p className="font-medium dir-ltr inline-block">
                              <a
                                href={`https://wa.me/${
                                  caseDetails.opponent.phone
                                    .replace(/[^0-9]/g, "")
                                    .startsWith("964")
                                    ? caseDetails.opponent.phone.replace(
                                        /[^0-9]/g,
                                        ""
                                      )
                                    : "964" +
                                      caseDetails.opponent.phone.replace(
                                        /[^0-9]/g,
                                        ""
                                      )
                                }`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:underline flex items-center gap-1"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  className="bi bi-whatsapp"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                                </svg>
                                {caseDetails.opponent.phone}
                              </a>
                            </p>
                          </div>
                        )}

                        {caseDetails.opponent.email && (
                          <div>
                            <p className="text-gray-500 text-sm">
                              البريد الإلكتروني
                            </p>
                            <p className="font-medium">
                              <a
                                href={`mailto:${caseDetails.opponent.email}`}
                                className="text-blue-600 hover:underline"
                              >
                                {caseDetails.opponent.email}
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {caseDetails.opponent.address && (
                      <div className="mt-2">
                        <p className="text-gray-500 text-sm">العنوان</p>
                        <p className="font-medium">
                          {caseDetails.opponent.address}
                        </p>
                      </div>
                    )}

                    {(caseDetails.opponent.lawyer_name ||
                      caseDetails.opponent.lawyer_phone) && (
                      <div className="mt-4 border-t pt-4">
                        <p className="font-semibold mb-2">معلومات المحامي</p>
                        <div>
                          {caseDetails.opponent.lawyer_name && (
                            <div>
                              <p className="text-gray-500 text-sm">
                                اسم المحامي
                              </p>
                              <p className="font-medium">
                                {caseDetails.opponent.lawyer_name}
                              </p>

                              {caseDetails.opponent.lawyer_phone && (
                                <div className="mt-3">
                                  <p className="text-gray-500 text-sm">
                                    هاتف المحامي
                                  </p>
                                  <p className="font-medium dir-ltr inline-block">
                                    <a
                                      href={`https://wa.me/${
                                        caseDetails.opponent.lawyer_phone
                                          .replace(/[^0-9]/g, "")
                                          .startsWith("964")
                                          ? caseDetails.opponent.lawyer_phone.replace(
                                              /[^0-9]/g,
                                              ""
                                            )
                                          : "964" +
                                            caseDetails.opponent.lawyer_phone.replace(
                                              /[^0-9]/g,
                                              ""
                                            )
                                      }`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-green-600 hover:underline flex items-center gap-1"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        className="bi bi-whatsapp"
                                        viewBox="0 0 16 16"
                                      >
                                        <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                                      </svg>
                                      {caseDetails.opponent.lawyer_phone}
                                    </a>
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {!caseDetails.opponent.lawyer_name &&
                            caseDetails.opponent.lawyer_phone && (
                              <div>
                                <p className="text-gray-500 text-sm">
                                  هاتف المحامي
                                </p>
                                <p className="font-medium dir-ltr inline-block">
                                  <a
                                    href={`https://wa.me/${
                                      caseDetails.opponent.lawyer_phone
                                        .replace(/[^0-9]/g, "")
                                        .startsWith("964")
                                        ? caseDetails.opponent.lawyer_phone.replace(
                                            /[^0-9]/g,
                                            ""
                                          )
                                        : "964" +
                                          caseDetails.opponent.lawyer_phone.replace(
                                            /[^0-9]/g,
                                            ""
                                          )
                                    }`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:underline flex items-center gap-1"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-whatsapp"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                                    </svg>
                                    {caseDetails.opponent.lawyer_phone}
                                  </a>
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">
                      {caseDetails?.opponent_name ||
                        "لم يتم تحديد معلومات الخصم"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading && !caseDetails) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!caseDetails) return null;

  return (
    <ErrorBoundary>
      <Helmet>
        <title>
          {`${
            caseDetails?.case_number
              ? `قضية رقم: ${caseDetails?.case_number}`
              : "تفاصيل القضية"
          } - نظام إدارة المكتب`}
        </title>
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg"
      >
        {/* Header Section */}
        <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-white">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-800">
                  {caseDetails?.title ||
                    `قضية رقم: ${caseDetails?.case_number || "غير محدد"}`}
                </h2>
                <Tag
                  color={statusColors[caseDetails?.status]?.color || "default"}
                  className="mr-2"
                >
                  {caseDetails?.status || "غير محدد"}
                </Tag>
              </div>
              <p className="text-gray-600 mt-1">
                {caseDetails?.court_name && `${caseDetails.court_name} - `}
                {caseDetails?.case_type || "غير محدد"}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEdit}
                className="flex items-center"
              >
                تعديل القضية
              </Button>
              <Button
                onClick={handlePrint}
                icon={<PrinterOutlined />}
                className="flex items-center"
              >
                طباعة
              </Button>
            </div>
          </div>

          {/* Case Status Card */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg border flex items-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                <FileOutlined className="text-blue-500 text-xl" />
              </div>
              <div>
                <p className="text-xs text-gray-500">رقم القضية</p>
                <p className="font-bold">
                  {caseDetails?.case_number || "غير محدد"}
                </p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border flex items-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                <span className="text-green-500 text-xl">👤</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">العميل</p>
                <p
                  className="font-bold truncate max-w-[150px]"
                  title={caseDetails?.client_name}
                >
                  {caseDetails?.client_name || "غير محدد"}
                </p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border flex items-center">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                <span className="text-purple-500 text-xl">📅</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">الجلسة القادمة</p>
                <p className="font-bold">
                  {caseDetails?.next_session_date || "لا يوجد"}
                </p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border flex items-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mr-4">
                <span className="text-amber-500 text-xl">💰</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">الأتعاب</p>
                <p className="font-bold">
                  {caseDetails?.agreed_fees
                    ? `${caseDetails.agreed_fees} دينار`
                    : "غير محدد"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex overflow-x-auto p-2 gap-2 no-scrollbar -mx-4 px-4 md:mx-0 md:px-2 flex-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
            
            {/* Dropdown menu for mobile */}
            <div className="md:hidden pl-2 pr-4">
              <Dropdown
                menu={{
                  items: tabs.map((tab) => ({
                    key: tab.id,
                    label: (
                      <div className="flex items-center gap-2">
                        <span>{tab.icon}</span>
                        <span>{tab.label}</span>
                      </div>
                    ),
                    onClick: () => setActiveTab(tab.id),
                  })),
                  selectedKeys: [activeTab],
                }}
                trigger={['click']}
                placement="bottomLeft"
              >
                <Button
                  icon={<MoreOutlined />}
                  type="text"
                  className="flex items-center justify-center"
                />
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Details Tab */}
          {activeTab === "details" && renderCaseDetails()}

          {/* Sessions Tab */}
          {activeTab === "sessions" && (
            <CaseSessions
              caseId={caseId}
              clientId={caseDetails?.client_id}
              sessions={sessions}
              onAddSession={handleAddSession}
              onEditSession={handleEditSession}
              onDeleteSession={handleDeleteSession}
              loading={sessionsLoading}
            />
          )}

          {/* Expenses Tab */}
          {activeTab === "expenses" && (
            <div>
              {expensesLoading ? (
                <div className="flex justify-center p-8">
                  <Spin tip="جاري تحميل المصاريف..." />
                </div>
              ) : (
                <div>
                  {expenses && expenses.length > 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border">
                      <div className="p-4 border-b flex justify-between items-center">
                        <h3 className="text-lg font-semibold">المصاريف</h3>
                        <Button type="primary" onClick={handleAddExpense}>
                          إضافة مصروف جديد
                        </Button>
                      </div>
                      <div className="p-4">
                        <ExpensesTable
                          expenses={expenses}
                          loading={expensesLoading}
                          onRefresh={fetchExpenses}
                          onEdit={handleEditExpense}
                          onView={handleViewExpense}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">
                        لا توجد مصاريف مسجلة لهذه القضية
                      </p>
                      <Button
                        type="primary"
                        className="mt-4"
                        onClick={handleAddExpense}
                      >
                        إضافة مصروف جديد
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === "payments" && (
            <div>
              {paymentsLoading ? (
                <div className="flex justify-center p-8">
                  <Spin tip="جاري تحميل المدفوعات..." />
                </div>
              ) : (
                <div>
                  {payments && payments.length > 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border">
                      <div className="p-4 border-b flex justify-between items-center">
                        <h3 className="text-lg font-semibold">المدفوعات</h3>
                        <Button type="primary" onClick={handleAddPayment}>
                          إضافة دفعة جديدة
                        </Button>
                      </div>
                      <div className="p-4">
                        <PaymentsTable
                          currentPayments={payments}
                          handleViewDetails={handleViewPaymentDetails}
                          handleDeletePayment={handleDeletePayment}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">
                        لا توجد مدفوعات مسجلة لهذه القضية
                      </p>
                      <Button
                        type="primary"
                        className="mt-4"
                        onClick={handleAddPayment}
                      >
                        إضافة دفعة جديدة
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === "documents" && (
            <div className="bg-white rounded-lg p-2 md:p-4">
              <div className="mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <FolderOpenOutlined className="text-blue-500" />
                      مستندات ومرفقات القضية
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      إجمالي المستندات:{" "}
                      <span className="font-medium text-blue-600">
                        {documents.length}
                      </span>
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="primary"
                      icon={<CloudUploadOutlined />}
                      onClick={() => setShowUploadArea(!showUploadArea)}
                      className="bg-gradient-to-r from-green-500 to-green-600 border-0 shadow-md hover:from-green-600 hover:to-green-700"
                    >
                      {showUploadArea ? "إخفاء الرفع" : "رفع مستند"}
                    </Button>
                    <Button
                      icon={<FolderOpenOutlined />}
                      onClick={fetchDocuments}
                      className="bg-gray-50 border-gray-300 hover:bg-gray-100"
                    >
                      تحديث
                    </Button>
                  </div>
                </div>

                {/* منطقة رفع الملفات السهلة */}
                {showUploadArea && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
                    <Upload.Dragger
                      name="file"
                      multiple={false}
                      beforeUpload={handleFilePreview}
                      showUploadList={false}
                      className="bg-white/70 border-2 border-dashed border-blue-300 hover:border-green-400 transition-all duration-300"
                      style={{ borderRadius: "12px" }}
                    >
                      <div className="py-6">
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined className="text-4xl text-blue-500" />
                        </p>
                        <p className="ant-upload-text text-gray-700 font-medium">
                          {uploading
                            ? "جاري رفع الملف..."
                            : "اسحب الملف هنا أو انقر للاختيار"}
                        </p>
                        <p className="ant-upload-hint text-gray-500 text-sm">
                          يمكنك رفع ملفات PDF، Word، Excel، صور، أو ملفات مضغوطة
                          (حد أقصى 10 ميجابايت)
                        </p>
                      </div>
                    </Upload.Dragger>

                    <div className="mt-3 flex justify-end">
                      <Button
                        size="small"
                        onClick={() => setShowUploadArea(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        إلغاء
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* قائمة المستندات */}
              {loading ? (
                <div className="flex justify-center p-8">
                  <Spin tip="جاري التحميل..." />
                </div>
              ) : (
                <DocumentsList
                  documents={documents}
                  onDownload={handleFileDownload}
                  onDelete={handleDeleteDocument}
                  onPreview={handlePreviewDocument}
                  onRefresh={fetchDocuments}
                  caseId={caseId}
                />
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === "notes" && (
            <div className="space-y-6">
              {/* ملاحظات القضية */}
              <div className="bg-white پ-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">
                  ملاحظات القضية
                </h3>
                <p className="text-gray-700 whitespace-pre-line min-h-[100px]">
                  {caseDetails?.notes || "لا توجد ملاحظات مضافة لهذه القضية."}
                </p>
                <Divider />
                <Button type="primary" onClick={handleEdit} className="mt-4">
                  تعديل الملاحظات
                </Button>
              </div>

              {/* ملاحظات الجلسات */}
              <div className="bg-white پ-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">
                  ملاحظات الجلسات
                </h3>

                {sessionsLoading ? (
                  <div className="flex justify-center پ-8">
                    <Spin tip="جاري تحميل الجلسات..." />
                  </div>
                ) : sessions && sessions.length > 0 ? (
                  <div className="space-y-4">
                    {sessions.map((session, index) => (
                      <div
                        key={index}
                        className="border rounded-lg پ-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <span className="font-bold ml-2">
                              تاريخ الجلسة:
                            </span>
                            <span>{session.session_date || "غير محدد"}</span>
                          </div>
                          <Tag
                            color={
                              new Date(session.session_date) > new Date()
                                ? "blue"
                                : session.status === "مكتملة"
                                ? "green"
                                : "default"
                            }
                          >
                            {new Date(session.session_date) > new Date()
                              ? "قادمة"
                              : "سابقة"}
                          </Tag>
                        </div>

                        {session.decision && (
                          <div className="mb-2">
                            <span className="font-bold ml-2">
                              قرار المحكمة:
                            </span>
                            <span>{session.decision}</span>
                          </div>
                        )}

                        {session.notes && (
                          <div className="mt-2">
                            <span className="font-bold block mb-1">
                              ملاحظات الجلسة:
                            </span>
                            <p className="bg-white پ-3 rounded border whitespace-pre-line text-gray-700">
                              {session.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">
                      لا توجد جلسات مسجلة لهذه القضية
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Edit Case Modal */}
      <EditCaseModal
        visible={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedCase(null);
        }}
        onSubmit={handleEditSubmit}
        initialData={selectedCase}
      />

      {/* مودل عرض تفاصيل الدفعة */}
      {isPaymentDetailsVisible && (
        <PaymentDetails
          payment={selectedPayment}
          onClose={() => {
            setIsPaymentDetailsVisible(false);
            setSelectedPayment(null);
          }}
        />
      )}

      {/* مودل إضافة دفعة جديدة */}
      {isAddPaymentModalVisible && (
        <AddPaymentModal
          visible={isAddPaymentModalVisible}
          onClose={() => {
            setIsAddPaymentModalVisible(false);
          }}
          onSuccess={() => {
            setIsAddPaymentModalVisible(false);
            // إعادة جلب المدفوعات بعد الإضافة
            fetchPayments();
          }}
          caseData={caseDetails}
        />
      )}

      {/* مودل إضافة مصروف جديد */}
      {isAddExpenseModalVisible && (
        <AddExpenseModal
          visible={isAddExpenseModalVisible}
          onClose={() => {
            setIsAddExpenseModalVisible(false);
            setEditingExpense(null);
            setIsViewExpenseModalVisible(false);
          }}
          onSuccess={() => {
            setIsAddExpenseModalVisible(false);
            setEditingExpense(null);
            setIsViewExpenseModalVisible(false);
            // إعادة جلب المصاريف بعد الإضافة
            fetchExpenses();
          }}
          caseData={caseDetails}
          editingExpense={editingExpense}
          isViewMode={isViewExpenseModalVisible}
        />
      )}

      {/* مودل تعديل مصروف */}
      {isEditExpenseModalVisible && (
        <EditExpenseModal
          visible={isEditExpenseModalVisible}
          onClose={handleCloseEditExpense}
          onSuccess={handleExpenseUpdated}
          expenseData={editingExpense}
          caseData={caseDetails}
        />
      )}

      {/* مودل عرض مصروف */}
      {isViewExpenseModalVisible && (
        <ViewExpenseModal
          visible={isViewExpenseModalVisible}
          onClose={handleCloseViewExpense}
          expenseData={viewingExpense}
        />
      )}

      {/* مودال تفاصيل المستند */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <FileOutlined className="text-blue-500" />
            تفاصيل المستند
          </div>
        }
        open={showFileDetailsModal}
        onOk={handleConfirmUpload}
        onCancel={() => {
          setShowFileDetailsModal(false);
          setFileToUpload(null);
          setDocumentDetails({ title: "", document_type: "", notes: "" });
        }}
        okText={uploading ? "جاري الرفع..." : "رفع المستند"}
        cancelText="إلغاء"
        confirmLoading={uploading}
        width={600}
        className="rtl-modal"
      >
        <div className="py-4">
          {fileToUpload && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">الملف المحدد:</p>
              <p className="font-medium text-gray-800">{fileToUpload.name}</p>
              <p className="text-xs text-gray-500">
                الحجم: {(fileToUpload.size / 1024 / 1024).toFixed(2)} ميجابايت
              </p>
            </div>
          )}

          <Form layout="vertical" className="space-y-4">
            <Form.Item label="عنوان المستند" required>
              <Input
                value={documentDetails.title}
                onChange={(e) =>
                  setDocumentDetails({
                    ...documentDetails,
                    title: e.target.value,
                  })
                }
                placeholder="أدخل عنوان وصفي للمستند"
                className="text-right"
              />
            </Form.Item>

            <Form.Item label="نوع المستند" required>
              <Select
                value={documentDetails.document_type}
                onChange={(value) =>
                  setDocumentDetails({
                    ...documentDetails,
                    document_type: value,
                  })
                }
                placeholder="اختر نوع المستند"
                className="text-right"
              >
                <Select.Option value="عريضة دعوى">عريضة دعوى</Select.Option>
                <Select.Option value="مذكرة قانونية">مذكرة قانونية</Select.Option>
                <Select.Option value="حكم قضائي">حكم قضائي</Select.Option>
                <Select.Option value="محضر جلسة">محضر جلسة</Select.Option>
                <Select.Option value="وكالة">وكالة</Select.Option>
                <Select.Option value="عقد">عقد</Select.Option>
                <Select.Option value="شهادة">شهادة</Select.Option>
                <Select.Option value="هوية شخصية">هوية شخصية</Select.Option>
                <Select.Option value="ملف PDF">ملف PDF</Select.Option>
                <Select.Option value="مستند Word">مستند Word</Select.Option>
                <Select.Option value="جدول Excel">جدول Excel</Select.Option>
                <Select.Option value="صورة">صورة</Select.Option>
                <Select.Option value="ملف مضغوط">ملف مضغوط</Select.Option>
                <Select.Option value="مستند قانوني">مستند قانوني</Select.Option>
                <Select.Option value="مراسلات">مراسلات</Select.Option>
                <Select.Option value="مستند عام">مستند عام</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="ملاحظات">
              <Input.TextArea
                value={documentDetails.notes}
                onChange={(e) =>
                  setDocumentDetails({
                    ...documentDetails,
                    notes: e.target.value,
                  })
                }
                placeholder="أضف أي ملاحظات إضافية حول المستند (اختياري)"
                rows={3}
                className="text-right"
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </ErrorBoundary>
  );
}
